import type {
  AnalysisSpec,
  AnalyticsBootstrap,
  BaselineComparison,
} from '@lognai/plugin-analytics';
import { memberLabel } from '../creator-helpers';

type Catalog = AnalyticsBootstrap['semanticCatalog'];
const selectClass = 'w-full rounded-md border border-border bg-surface px-2 py-2 text-sm';

export const lastCompleteMonths = (now = new Date()): [string, string] => {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 5, 1));
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
};

export function BaselineComparisonEditor({
  analysis,
  catalog,
  onChange,
  disabled,
}: {
  analysis: AnalysisSpec;
  catalog: Catalog;
  onChange: (patch: Partial<AnalysisSpec>) => void;
  disabled?: boolean;
}) {
  const comparison = analysis.baselineComparison;
  const current = catalog[analysis.view];
  const candidates = Object.entries(catalog).filter(
    ([view, source]) =>
      view.startsWith('dataset_') &&
      source.timeDimensions.length &&
      Object.values(source.measures).some(
        (metric) => metric.additive && metric.unit === 'currency' && metric.currency
      )
  );
  const currentMeasures = Object.entries(current?.measures ?? {}).filter(
    ([, metric]) => metric.additive && metric.unit === 'currency'
  );
  if (
    !currentMeasures.length ||
    !current?.timeDimensions.length ||
    analysis.view.startsWith('dataset_')
  )
    return null;
  const selected = comparison && catalog[comparison.view];
  const baselineMeasures = Object.entries(selected?.measures ?? {}).filter(
    ([, metric]) => metric.additive && metric.unit === 'currency' && metric.currency
  );
  const currencyDimension = current.measures[analysis.measures[0]]?.currencyDimension;
  const change = (patch: Partial<BaselineComparison>) => {
    if (!comparison) return;
    const next = { ...comparison, ...patch };
    const info = catalog[next.view]?.measures[next.measure];
    next.currency = info?.currency ?? next.currency;
    onChange({
      baselineComparison: next,
      ...(currencyDimension
        ? {
            filters: [
              ...analysis.filters.filter((filter) => filter.member !== currencyDimension),
              { member: currencyDimension, operator: 'equals', values: [next.currency] },
            ],
          }
        : {}),
    });
  };
  const filterMembers = [
    ...new Set(
      analysis.filters
        .map((filter) => filter.member)
        .filter((member) => member !== currencyDimension)
    ),
  ];
  return (
    <fieldset className="space-y-3 rounded-lg border border-border p-3" disabled={disabled}>
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          aria-label="Compare with a baseline dataset"
          checked={!!comparison}
          disabled={disabled || (!comparison && !candidates.length)}
          onChange={(event) => {
            if (!event.target.checked) {
              onChange({ baselineComparison: undefined });
              return;
            }
            const [view, source] = candidates[0];
            const [measure, info] = Object.entries(source.measures).find(
              ([, metric]) => metric.additive && metric.unit === 'currency' && metric.currency
            )!;
            const currentMeasure = current.measures[analysis.measures[0]]?.additive
              ? analysis.measures[0]
              : currentMeasures[0][0];
            const currencyMember = current.measures[currentMeasure]?.currencyDimension;
            const member =
              current.timeDimensions.find((member) => member.endsWith('.rfq_created_at')) ??
              analysis.timeDimension?.member ??
              current.timeDimensions[0];
            onChange({
              baselineComparison: {
                view,
                measure,
                timeDimension: source.timeDimensions[0],
                yearsAgo: 1,
                currency: info.currency!,
                currentLabel: current.measures[currentMeasure].label,
                baselineLabel: 'Prior-year baseline',
                filterMappings: [],
              },
              measures: [currentMeasure],
              dimensions: [],
              drillPath: [],
              calculatedMetrics: undefined,
              timeDimension: { member, granularity: 'month', dateRange: lastCompleteMonths() },
              template: 'stacked_column',
              display: { ...analysis.display, groupedColumns: true },
              ...(currencyMember
                ? {
                    filters: [
                      ...analysis.filters.filter((filter) => filter.member !== currencyMember),
                      { member: currencyMember, operator: 'equals', values: [info.currency!] },
                    ],
                  }
                : {}),
            });
          }}
        />
        Compare with a baseline dataset
      </label>
      <p className="text-xs leading-5 text-muted-foreground">
        Compare complete months with a published prior-year dataset. Each source is summed
        independently in the same currency. Missing months are excluded, never treated as zero.
      </p>
      {!candidates.length && (
        <p className="text-xs text-muted-foreground">
          Publish a dataset with a date field and a currency amount with Sum enabled to use this
          comparison.
        </p>
      )}
      {comparison && (
        <>
          <label className="block space-y-1 text-xs">
            Baseline dataset
            <select
              aria-label="Baseline dataset"
              className={selectClass}
              value={comparison.view}
              onChange={(event) => {
                const source = catalog[event.target.value];
                const measure = Object.keys(source.measures).find(
                  (member) => source.measures[member].additive && source.measures[member].currency
                )!;
                change({
                  view: event.target.value,
                  measure,
                  timeDimension: source.timeDimensions[0],
                  filterMappings: [],
                });
              }}
            >
              <option value="" disabled>
                Choose a published dataset
              </option>
              {candidates.map(([view, source]) => (
                <option key={view} value={view}>
                  {source.title ?? view}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-xs">
            Baseline amount
            <select
              aria-label="Baseline amount"
              className={selectClass}
              value={comparison.measure}
              onChange={(event) => change({ measure: event.target.value })}
            >
              {baselineMeasures.map(([member, info]) => (
                <option key={member} value={member}>
                  {info.label} ({info.currency})
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-xs">
            Baseline date
            <select
              aria-label="Baseline date"
              className={selectClass}
              value={comparison.timeDimension}
              onChange={(event) => change({ timeDimension: event.target.value })}
            >
              {selected?.timeDimensions.map((member) => (
                <option key={member} value={member}>
                  {memberLabel(member, selected)}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-xs">
            Years earlier
            <select
              aria-label="Baseline years earlier"
              className={selectClass}
              value={comparison.yearsAgo}
              onChange={(event) => change({ yearsAgo: Number(event.target.value) })}
            >
              {[1, 2, 3, 4, 5].map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          {(['currentLabel', 'baselineLabel'] as const).map((key) => (
            <label key={key} className="block space-y-1 text-xs">
              {key === 'currentLabel' ? 'Current series label' : 'Baseline series label'}
              <input
                aria-label={
                  key === 'currentLabel' ? 'Current series label' : 'Baseline series label'
                }
                className={selectClass}
                maxLength={100}
                value={comparison[key]}
                onChange={(event) => change({ [key]: event.target.value })}
              />
            </label>
          ))}
          {filterMembers.map((member) => (
            <label key={member} className="block space-y-1 text-xs">
              Match filter: {memberLabel(member, current)}
              <select
                className={selectClass}
                aria-label={`Baseline field for ${memberLabel(member, current)}`}
                value={
                  comparison.filterMappings.find((mapping) => mapping.current === member)
                    ?.baseline ?? ''
                }
                onChange={(event) =>
                  change({
                    filterMappings: [
                      ...comparison.filterMappings.filter((mapping) => mapping.current !== member),
                      ...(event.target.value
                        ? [{ current: member, baseline: event.target.value }]
                        : []),
                    ],
                  })
                }
              >
                <option value="">Choose the equivalent field, or remove this filter</option>
                {selected?.dimensions
                  .filter((member) => !selected.timeDimensions.includes(member))
                  .map((member) => (
                    <option key={member} value={member}>
                      {memberLabel(member, selected)}
                    </option>
                  ))}
              </select>
            </label>
          ))}
          <p className="text-xs leading-5 text-muted-foreground">
            Currency: {comparison.currency}. The dates above define this chart’s comparison period;
            rolling dashboard date presets do not replace it. Choose the actual RFQ creation date
            for an RFQ creation cohort. Reduction is baseline minus current value; it does not
            establish the cause of a change.
          </p>
        </>
      )}
    </fieldset>
  );
}
